import { push } from 'connected-react-router';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { ANALYTICS_EVENTS } from 'src/app/analytics/analyticsEvents';
import { errorBoundary, withStandardPageAndLoading } from 'src/app/hocs';
import { AccountPageAddAddress } from 'src/app/router/paths';
import {
  clearAddressValidationAction,
  clearEditAddressState,
  closeModal,
  deleteAccountAddress,
  deleteInsuranceCard,
  deletePaymentCard,
  editAddress,
  fetchAccountPageData,
  openModal,
  segmentAnalyticsEvent,
  segmentTrack,
  voidPrimaryInsurance,
  voidSecondaryInsurance,
} from 'src/app/state/actions';
import { getInsurance } from 'src/app/state/actions/insurance';
import {
  getBffDeliveryAddress,
  getDeliveryAddresses,
  getIsInsuranceUploadSuccessful,
  getPaymentCards,
} from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';

import { Renderer } from '.';

function makeMapStateToProps() {
  return (state: RootState) => {
    const cards = getPaymentCards(state);
    const addresses = getDeliveryAddresses(state);

    const insurances = state?.insurances?.list ?? null;
    const insuranceSubmittedOn = state?.insurances?.pendingStatus?.submittedOn ?? null;
    const isInsuranceUploadSuccessful = getIsInsuranceUploadSuccessful(state);

    const bffDeliveryAddress = getBffDeliveryAddress(state);

    return {
      cards,
      addresses,
      isInsuranceUploadSuccessful,
      insurances,
      insuranceSubmittedOn,
      bffDeliveryAddress,
    };
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchDeletePaymentCard: (accountId, cardId) => dispatch(deletePaymentCard(accountId, cardId)),
  dispatchDeleteInsurance: (id, patient_id) => dispatch(deleteInsuranceCard(id, patient_id)),
  dispatchAccountDeleteAddress: (addressId, accountId) => dispatch(deleteAccountAddress(addressId, accountId)),
  dispatchOpenModal: ({ modalType, handleConfirmClick, confirmMessage }) =>
    dispatch(openModal({ modalType, handleConfirmClick, confirmMessage })),
  dispatchEditAddress: address => {
    return dispatch(editAddress(address));
  },
  dispatchTrackAddressChange: addressChange =>
    dispatch(segmentAnalyticsEvent(`${ANALYTICS_EVENTS.addressChangePrefix}${addressChange}${' Address'}`)),
  dispatchCloseModal: () => dispatch(closeModal()),
  dispatchGotoAddAddress: () => {
    dispatch(clearAddressValidationAction());
    dispatch(clearEditAddressState());
    dispatch(push(AccountPageAddAddress.toPath()));
  },
  dispatchGetInsurance: accountId => {
    dispatch(getInsurance(accountId));
  },
  dispatchAddInsuranceSegmentEvent: attributes =>
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.addInsurance, { attributes })),
  dispatchDeleteInsuranceModalSegmentEvent: attributes =>
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.deleteViewInsurance, { attributes })),
  dispatchDeleteInsuranceSegmentEvent: attributes =>
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.deleteSuccesfulInsurance, { attributes })),
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
  dispatchFetchAccountData: () => {
    dispatch(fetchAccountPageData());
  },
  dispatchVoidPrimaryInsurance: (insuredId: string) => dispatch(voidPrimaryInsurance(insuredId)),
  dispatchVoidSecondaryInsurance: (insuredId: string, bin: string, cardholderNumber: string) =>
    dispatch(voidSecondaryInsurance(insuredId, bin, cardholderNumber)),
});

const connector = connect(makeMapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

export const AccountPage = compose(
  errorBoundary,
  connector,
  /**
   * `isLoading` prop is undefined
   */
  withStandardPageAndLoading('Your Account')
)(Renderer);
